/**
 * "Save to Baseline" — pick an existing baseline or name a new one for the current selection.
 *
 * Structure follows TargetSelectionModal (Portal into #main-content, `pagesettings-overlay` /
 * `pagesettings-modal`) so it looks and behaves like the target picker already on this page.
 */
import { For, Show, createMemo } from 'solid-js';
import { Portal } from 'solid-js/web';
import { NEW_BASELINE_OPTION } from '../../hooks/useBaselineSave';

export interface BaselineSaveModalProps {
  show: boolean;
  onClose: () => void;
  /** Only id and name are read; whole rows from the service are assignable. */
  baselines: Array<{ baseline_id: number; name: string }>;
  selectedKey: string;
  onSelectedKeyChange: (value: string) => void;
  isNamingNew: boolean;
  newName: string;
  onNewNameChange: (value: string) => void;
  selectedEventCount: number;
  saving: boolean;
  saveError: string | null;
  saveMessage: string | null;
  onSave: () => void;
}

export default function BaselineSaveModal(props: BaselineSaveModalProps) {
  const canSave = createMemo(() => {
    if (props.saving) { return false; }
    if (props.selectedEventCount <= 0) { return false; }
    if (props.isNamingNew) { return props.newName.trim().length > 0; }
    return Boolean(props.selectedKey);
  });

  const eventSummary = () =>
    `${props.selectedEventCount} selected event${props.selectedEventCount === 1 ? '' : 's'}`;

  return (
    <Show when={props.show}>
      <Portal
        mount={typeof document !== 'undefined'
          ? (document.getElementById('main-content') || document.body)
          : undefined}
      >
        <div class="pagesettings-overlay" onClick={props.onClose}>
          <div
            class="pagesettings-modal target_modal baseline_modal"
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'relative', 'z-index': 10001 }}
          >
            <div
              class="flex justify-between items-center p-4 border-b"
              style="border-color: var(--color-border-primary);"
            >
              <h2 class="text-lg font-semibold" style="color: var(--color-text-primary);">
                Save to Baseline
              </h2>
              <button
                type="button"
                onClick={props.onClose}
                class="text-gray-500 hover:text-gray-700 transition-colors"
                style="color: var(--color-text-secondary);"
                aria-label="Close"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div class="p-6">
              <p class="baseline-modal-summary" style="color: var(--color-text-secondary);">
                {eventSummary()}
              </p>
              <p class="baseline-modal-hint">
                Performance and maneuvers share one baseline. Pick an existing name, or add a new one.
              </p>

              <Show when={props.saveError}>
                <p class="baseline-modal-error" role="alert">{props.saveError}</p>
              </Show>
              <Show when={props.saveMessage}>
                <p class="baseline-modal-message">{props.saveMessage}</p>
              </Show>

              <Show
                when={props.isNamingNew}
                fallback={
                  <select
                    class="target-modal-select"
                    value={props.selectedKey}
                    onChange={(e) => props.onSelectedKeyChange(e.currentTarget.value)}
                  >
                    <For each={props.baselines}>
                      {(baseline) => (
                        <option value={String(baseline.baseline_id)}>{baseline.name}</option>
                      )}
                    </For>
                    <option value={NEW_BASELINE_OPTION}>New baseline</option>
                  </select>
                }
              >
                <input
                  type="text"
                  class="target-modal-select"
                  placeholder="Baseline name"
                  value={props.newName}
                  onInput={(e) => props.onNewNameChange(e.currentTarget.value)}
                />
              </Show>

              <Show when={props.selectedKey === NEW_BASELINE_OPTION && !props.isNamingNew}>
                <p class="baseline-modal-hint">Click Save to enter a name. An existing name adds these events to that baseline.</p>
              </Show>
              <Show when={!props.baselines.length && props.isNamingNew}>
                <p class="baseline-modal-hint">Enter a name. If that baseline already exists, these events are added to it.</p>
              </Show>

              <div class="target-modal-actions">
                <button
                  type="button"
                  onClick={props.onClose}
                  class="px-4 py-2 text-sm rounded-md transition-colors"
                  style="background-color: var(--color-bg-button-secondary); color: var(--color-text-button-secondary);"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={props.onSave}
                  disabled={!canSave()}
                  class="px-4 py-2 text-sm rounded-md transition-colors font-medium"
                  style={`background-color: ${canSave() ? '#16a34a' : 'var(--color-bg-button)'}; color: var(--color-text-inverse); ${canSave() ? '' : 'cursor: not-allowed; opacity: 0.6;'}`}
                >
                  {props.saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
